import type { UseFormReturn } from 'react-hook-form';
import {
  Labeled,
  OptionCard,
  Radio,
  RadioGroup,
  Switch,
  TextAreaField,
  TextInputField,
} from '@sixui/ui';
import { densityTokens } from '@sixui/ui/themes/base/density.stylex';
import { scaleTokens } from '@sixui/ui/themes/base/scale.stylex';
import { spacingTokens } from '@sixui/ui/themes/base/spacing.stylex';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IUserBadgeFormValues } from './UserBadgeForm';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { Link } from '~/components/atoms/Link';
import { CustomColorInputField } from '~/components/organisms/fields/CustomColorInputField';
import { IapProductSelector } from '~/components/organisms/fields/IapProductSelector';
import { IFormMode } from '~/helpers/types';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { getAppSettings } from '~/lib/appSettings';

export type IUserBadgeFormMainSectionProps = IContainerProps & {
  form: UseFormReturn<IUserBadgeFormValues>;
  mode: IFormMode;
  iapEnabled?: boolean;
  appId: string;
};

const MIN_DENSITY = -1;
const MAX_DENSITY = 0;
const DENSITY = `${densityTokens.interval} * clamp(${MIN_DENSITY}, ${densityTokens.density}, ${MAX_DENSITY}) * ${scaleTokens.scale}`;

const styles = stylex.create({
  options: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridAutoRows: '1fr',
    columnGap: spacingTokens.padding$2,
    rowGap: `calc(${spacingTokens.padding$2} + ${DENSITY})`,
  },
});

export const UserBadgeFormMainSection: React.FC<
  IUserBadgeFormMainSectionProps
> = (props) => {
  const { sx, form, iapEnabled, appId } = props;
  const { t } = useTranslation();
  const appSettings = getAppSettings();
  const appQuery = useAppQuery({ appId });

  return (
    <FormSection sx={sx}>
      <Labeled label={t('common.name')} required>
        <FormField
          name="name"
          control={form.control}
          as={TextInputField}
          forwardError
        />
      </Labeled>
      <Labeled
        label={t('common.description')}
        required={!!form.watch('productId')}
      >
        <FormField
          name="description"
          control={form.control}
          as={TextAreaField}
          style={{
            // This is a hack for TextAreaField to work in Firefox.
            // TODO: Remove this hack once fixed in @sixui/ui.
            whiteSpace: 'revert',
          }}
          forwardError
        />
      </Labeled>
      <Labeled label={t('common.color')} required>
        <FormField
          name="color"
          control={form.control}
          as={CustomColorInputField}
          forwardError
          palette="app"
        />
      </Labeled>
      <Labeled
        label={t('UserBadgeFormMainSection.field.productId.label')}
        supportingText={t(
          'UserBadgeFormMainSection.field.productId.supportingText',
        )}
        disabled={!iapEnabled}
      >
        <FormField
          name="productId"
          control={form.control}
          as={IapProductSelector}
          forwardError
          productType="recurring"
          rules={{
            onChange: (event: React.ChangeEvent<HTMLSelectElement>) => {
              if (event.target.value) {
                form.setValue('management', 'public');
              }
            },
          }}
          availableProductIds={appQuery?.data?.settings?.iap?.products}
        />
      </Labeled>
      {!iapEnabled ? (
        <p>
          {t('error.iapNotEnabled')}{' '}
          <Link to={`mailto:${appSettings.contact.email}`}>
            {t('common.pleaseContactUs')}
          </Link>
          .
        </p>
      ) : null}
      <Labeled
        label={t('UserBadgeFormMainSection.field.access.label')}
        supportingText={t(
          'UserBadgeFormMainSection.field.access.supportingText',
        )}
        required
      >
        <FormField
          name="access"
          control={form.control}
          as={RadioGroup}
          rules={{ required: true }}
          sx={styles.options}
        >
          <OptionCard
            as={Radio}
            label={t(
              'UserBadgeFormMainSection.field.access.option.hidden.label',
            )}
            value="hidden"
          >
            {t(
              'UserBadgeFormMainSection.field.access.option.hidden.supportingText',
            )}
          </OptionCard>
          <OptionCard
            as={Radio}
            label={t(
              'UserBadgeFormMainSection.field.access.option.teaser.label',
            )}
            value="teaser"
          >
            {t(
              'UserBadgeFormMainSection.field.access.option.teaser.supportingText',
            )}
          </OptionCard>
          <OptionCard
            as={Radio}
            label={t(
              'UserBadgeFormMainSection.field.access.option.preview.label',
            )}
            value="preview"
          >
            {t(
              'UserBadgeFormMainSection.field.access.option.preview.supportingText',
            )}
          </OptionCard>
          <OptionCard
            as={Radio}
            label={t(
              'UserBadgeFormMainSection.field.access.option.notifications.label',
            )}
            value="notifications"
          >
            {t(
              'UserBadgeFormMainSection.field.access.option.notifications.supportingText',
            )}
          </OptionCard>
        </FormField>
      </Labeled>
      <Labeled
        label={t('UserBadgeFormMainSection.field.management.label')}
        supportingText={t(
          'UserBadgeFormMainSection.field.management.supportingText',
        )}
        readOnly={!!form.watch('productId')}
        required
      >
        <FormField
          name="management"
          control={form.control}
          as={RadioGroup}
          rules={{ required: true }}
          sx={styles.options}
        >
          <OptionCard
            as={Radio}
            label={t(
              'UserBadgeFormMainSection.field.management.option.private-internal.label',
            )}
            value="private-internal"
          >
            {t(
              'UserBadgeFormMainSection.field.management.option.private-internal.supportingText',
            )}
          </OptionCard>
          <OptionCard
            as={Radio}
            label={t(
              'UserBadgeFormMainSection.field.management.option.private-visible.label',
            )}
            value="private-visible"
          >
            {t(
              'UserBadgeFormMainSection.field.management.option.private-visible.supportingText',
            )}
          </OptionCard>
          <OptionCard
            as={Radio}
            label={t(
              'UserBadgeFormMainSection.field.management.option.request.label',
            )}
            value="request"
          >
            {t(
              'UserBadgeFormMainSection.field.management.option.request.supportingText',
            )}
          </OptionCard>
          <OptionCard
            as={Radio}
            label={t(
              'UserBadgeFormMainSection.field.management.option.public.label',
            )}
            value="public"
          >
            {t(
              'UserBadgeFormMainSection.field.management.option.public.supportingText',
            )}
          </OptionCard>
        </FormField>
      </Labeled>
      <Labeled
        label={t('UserBadgeFormMainSection.field.isDefault.label')}
        supportingText={t(
          'UserBadgeFormMainSection.field.isDefault.supportingText',
        )}
        labelPosition="right"
      >
        <FormField name="isDefault" control={form.control} as={Switch} />
      </Labeled>
      <Labeled
        label={t('UserBadgeFormMainSection.field.privacyPolicyUrl.label')}
        required={!!form.watch('productId')}
      >
        <FormField
          name="privacyPolicyUrl"
          control={form.control}
          as={TextInputField}
          forwardError
          type="url"
        />
      </Labeled>
    </FormSection>
  );
};
